import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Stack,
  Chip,
  Box,
  Button,
  Divider,
  Fade
} from '@mui/material';
import {
  Quiz as QuizIcon,
  AccessTime as TimeIcon,
  School as SchoolIcon,
  CalendarToday as DateIcon,
  Star as StarIcon,
  Psychology as AiIcon,
  Android as AndroidIcon,
  Book as BookIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import AppDownloadQrModal from './AppDownloadQrModal';

/**
 * Reusable Paper Card Component with elegant design
 * Can be used for Previous Papers, Generated Papers, Exam Papers, etc.
 */
const PaperCard = ({
  paper,
  onClick,
  index = 0,
  variant = 'default', // 'default', 'generated', 'exam', 'previous'
  showDescription = true,
  showTotalMarks = true,
  customFields = [],
  actionButtonText = 'Start Exam',
  disabled = false,
  // These are PaperCard-specific props — destructure them out so they
  // are never forwarded to the MUI Card (and then to the DOM div).
  showAppDownloadQr,
  playStoreUrl,
  qrImageUrl,
  qrSize,
  ...props
}) => {
  const [showQr, setShowQr] = useState(false);
  const offersAndroidDownload = disabled && showAppDownloadQr;
  // Get the appropriate icon based on variant
  const getVariantIcon = () => {
    switch (variant) {
      case 'generated':
        return <AiIcon color="primary" />;
      case 'exam':
        return <QuizIcon color="primary" />;
      case 'previous':
        return <BookIcon color="primary" />;
      default:
        return <QuizIcon color="primary" />;
    }
  };

  // Get difficulty color
  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'error';
      default: return 'default';
    }
  };

  // Format date - handles both Firebase timestamps and regular dates
  const formatDate = (dateField) => {
    try {
      // Handle Firebase timestamp format { _seconds: number, _nanoseconds: number }
      if (dateField && typeof dateField === 'object' && dateField._seconds) {
        const date = new Date(dateField._seconds * 1000);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      // Handle regular date string
      if (dateField) {
        return new Date(dateField).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      return 'Unknown';
    } catch {
      return 'Unknown';
    }
  };

  // Get display title
  const getTitle = () => {
    return paper.title || paper.name || paper.paperName || `Paper ${paper.id?.slice(-8) || ''}`;
  };

  // Get question count
  const getQuestionCount = () => {
    return paper.questionCount || paper.totalQuestions || paper.questions?.length || 0;
  };

  // Get duration
  const getDuration = () => {
    return paper.duration || paper.durationMinutes || 60;
  };

  return (
    <>
      <Fade in timeout={300 + index * 100}>
      <Card 
        sx={{ 
          height: '100%',
          transition: 'all 0.3s ease',
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
          },
          ...props.sx
        }}
        {...props}
      >
        <CardActionArea
          onClick={(e) => {
            if (offersAndroidDownload) {
              setShowQr(true);
              return;
            }
            if (disabled) return;
            onClick && onClick(e);
          }}
          disabled={disabled && !offersAndroidDownload}
          aria-label={offersAndroidDownload ? `Download the Android app to open ${getTitle()}` : undefined}
          sx={{ height: '100%', p: 0 }}
        >
          <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              {getVariantIcon()}
              <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
                {getTitle()}
              </Typography>
              {paper.status && (
                <Chip 
                  label={paper.status} 
                  size="small" 
                  color={paper.status === 'ready' ? 'success' : 'default'}
                  variant="outlined"
                  sx={{ borderRadius: '16px', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}
                />
              )}
            </Stack>

            {/* Description */}
            {showDescription && paper.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {paper.description}
              </Typography>
            )}

            {/* Details */}
            <Stack spacing={2} sx={{ flex: 1 }}>
              {/* Question Count and Duration */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <QuizIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {getQuestionCount()} Questions
                </Typography>
                <Divider orientation="vertical" flexItem />
                <TimeIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {getDuration()} min
                </Typography>
              </Stack>

              {/* Subject/Category and Difficulty */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <SchoolIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {paper.subject || paper.category || paper.examId || 
                   (paper.sourcePaperCount ? `From ${paper.sourcePaperCount} papers` : 'Mixed')}
                </Typography>
                {paper.difficulty && (
                  <>
                    <Divider orientation="vertical" flexItem />
                    <Chip 
                      label={paper.difficulty} 
                      size="small" 
                      color={getDifficultyColor(paper.difficulty)}
                      variant="outlined"
                      sx={{ borderRadius: '16px', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}
                    />
                  </>
                )}
              </Stack>

              {/* Date */}
              {(paper.createdAt || paper.year || paper.generatedAt) && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DateIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {paper.createdAt ? `Created: ${formatDate(paper.createdAt)}` :
                     paper.generatedAt ? `Generated: ${formatDate(paper.generatedAt)}` :
                     paper.year ? `Year: ${paper.year}` : ''}
                  </Typography>
                </Stack>
              )}

              {/* Total Marks */}
              {showTotalMarks && paper.totalMarks && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <StarIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Total Marks: {paper.totalMarks}
                  </Typography>
                </Stack>
              )}

              {/* Custom Fields */}
              {customFields.map((field, idx) => (
                <Stack key={idx} direction="row" alignItems="center" spacing={1}>
                  {field.icon}
                  <Typography variant="body2" color="text.secondary">
                    {field.text}
                  </Typography>
                </Stack>
              ))}

              {/* Tags (if any) */}
              {paper.tags && paper.tags.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {paper.tags.slice(0, 3).map((tag, idx) => (
                    <Chip 
                      key={idx}
                      label={tag} 
                      size="small" 
                      variant="outlined" 
                      sx={{ fontSize: '0.65rem', borderRadius: '16px', fontWeight: 700 }}
                    />
                  ))}
                  {paper.tags.length > 3 && (
                    <Chip 
                        label={`+${paper.tags.length - 3}`} 
                        size="small" 
                        variant="outlined" 
                        sx={{ fontSize: '0.65rem', borderRadius: '16px', fontWeight: 700 }}
                      />
                  )}
                </Stack>
              )}
            </Stack>

            {/* Action Area */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                  component="div"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={disabled && !offersAndroidDownload}
                  startIcon={offersAndroidDownload ? <AndroidIcon /> : undefined}
                  sx={{
                    borderRadius: '16px',
                    fontWeight: 800,
                    textTransform: 'none'
                  }}
                >
                  {offersAndroidDownload ? 'Download Android App' : actionButtonText}
                </Button>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
      </Fade>

      {offersAndroidDownload && (
        <AppDownloadQrModal
          open={showQr}
          playStoreUrl={playStoreUrl}
          qrImageUrl={qrImageUrl}
          qrSize={qrSize}
          onClose={() => setShowQr(false)}
        />
      )}
    </>
  );
};

export default PaperCard;
